import React, { useState, useEffect, useMemo } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./ProductCarousel.scss";
import SearchResult from "./SearchResult";
import TfAddToList from "./TfAddToList";
import { CoreButton, CoreLoader } from "./komodo";
import { getString } from "../utils/localizationHelper";

const ProductCarousel = ({
  products,
  items, // New prop to support mixed items (products + search results)
  onProductClick,
  onAddToCart,
  isBestSolution = false,
  isAdditionalProducts = false,
}) => {
  const [isReady, setIsReady] = useState(false);

  // Use items if provided, otherwise use products for backward compatibility
  const carouselItems = useMemo(() => {
    return (
      items ||
      (products
        ? products.map((product) => ({ type: "product", product }))
        : [])
    );
  }, [items, products]);

  // Calculate slidesToShow based on window width (only called once during settings creation)
  const getSlidesToShow = () => {
    if (typeof window === "undefined") return 4;
    const width = window.innerWidth;
    if (width <= 575) return 1;
    if (width <= 767) return 2;
    if (width <= 992) return 3;
    if (width <= 1200) return 3;
    return 4;
  };

  // Wait for component to be fully mounted and items to be available
  useEffect(() => {
    if (carouselItems && carouselItems.length > 0) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => setIsReady(true), 100);
      return () => clearTimeout(timer);
    }
  }, [carouselItems]);

  // After slick-slide is populated, set all .tf-result-card heights to the tallest
  useEffect(() => {
    if (!isReady) return;

    const equalizeHeights = () => {
      // Query all .tf-result-card elements inside visible slick-slides
      const cards = Array.from(
        document.querySelectorAll(
          ".slick-slide:not(.slick-cloned) .tf-result-card"
        )
      ).filter((card) => card.offsetParent !== null);

      if (cards.length === 0) return;

      // Reset heights first to get natural height
      cards.forEach((card) => {
        card.style.minHeight = "";
        card.style.height = "";
      });

      // Find max height
      let maxHeight = 0;
      cards.forEach((card) => {
        const h = card.offsetHeight;
        if (h > maxHeight) maxHeight = h;
      });

      // Set all cards to maxHeight
      cards.forEach((card) => {
        card.style.minHeight = maxHeight + "px";
      });
    };

    // Run after a delay to ensure DOM is ready
    const timer = setTimeout(equalizeHeights, 200);

    // Cleanup on unmount
    return () => {
      clearTimeout(timer);
      const cards = Array.from(
        document.querySelectorAll(".slick-slide .tf-result-card")
      );
      cards.forEach((card) => {
        card.style.minHeight = "";
        card.style.height = "";
      });
    };
  }, [isReady, carouselItems.length]);

  const PrevArrow = ({ currentSlide, slideCount, ...props }) => (
    <button
      {...props}
      className="tf-slider-btn tf-prev slick-arrow"
      style={{ display: "inline-block" }}
      aria-label="Get previous item"
      data-uw-rm-empty-ctrl=""
    >
      <i className="tf-komodo-24-icon-chevron-left-mono" />
    </button>
  );

  const NextArrow = ({ currentSlide, slideCount, ...props }) => (
    <button
      {...props}
      className="tf-slider-btn tf-next slick-arrow"
      style={{ display: "inline-block" }}
      aria-label="Get next item"
      data-uw-rm-empty-ctrl=""
    >
      <i className="tf-komodo-24-icon-chevron-right-mono" />
    </button>
  );

  const settings = {
    infinite: carouselItems && carouselItems.length > 1, // Only infinite if multiple items
    slidesToShow: getSlidesToShow(), // Initial value based on current window width
    slidesToScroll: 1,
    autoplay: false,
    arrows: true,
    dots: false,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    adaptiveHeight: false,
    variableWidth: false,
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 992,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 767,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 575,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  // Enhanced validation
  if (
    !carouselItems ||
    !Array.isArray(carouselItems) ||
    carouselItems.length === 0
  ) {
    return null;
  }

  // Don't render until component is ready
  if (!isReady) {
    return (
      <div className="tf-product-slider">
        <CoreLoader
          variant="circular"
          functionality="indeterminate"
          size="small"
          information="false"
          alignment="center"
        />
      </div>
    );
  }

  return (
    <div
      className={`tf-product-slider ${
        isAdditionalProducts ? "additional-products-carousel" : ""
      }`}
      style={{ width: "100%", overflow: "hidden" }}
    >
      <Slider {...settings} key={carouselItems.length}>
        {carouselItems.map((item, index) => {
          if (!item || typeof item !== "object") return null;

          // Handle search result items
          if (item.type === "search" || item.type === "button") {
            return (
              <div key={`${item.category}-${index}`} className="product">
                <SearchResult item={item} />
              </div>
            );
          }

          // Handle product items
          if (item.type === "product") {
            const product = item.product;
            if (!product) return null;

            return (
              <div key={product.sku || index} className="product">
                <div className="tf-result-card">
                  <div className="tf-result-card-body">
                    <div className="tf-product-image">
                      {product.url ? (
                        <a
                          className="tf-link"
                          href={product.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() =>
                            onProductClick &&
                            onProductClick(product, isBestSolution)
                          }
                        >
                          <img
                            src={product.image}
                            alt={product.name || "Product"}
                          />
                        </a>
                      ) : (
                        <span>
                          <img
                            src={product.image}
                            alt={product.name || "Product"}
                          />
                        </span>
                      )}
                    </div>
                    <div className="tf-r-p-col">
                      <div className="product-catalog">
                        {getString("results.catalog_no")}{" "}
                        <span>{product.formattedSku || product.sku}</span>
                      </div>
                      <div className="product-brand">{product.brand}</div>
                      <div className="product-name">
                        {product.url ? (
                          <a
                            className="tf-link"
                            href={product.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() =>
                              onProductClick &&
                              onProductClick(product, isBestSolution)
                            }
                            dangerouslySetInnerHTML={{ __html: product.name }}
                          />
                        ) : (
                          <span
                            className="no-url"
                            dangerouslySetInnerHTML={{ __html: product.name }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="product-specs">
                    <span className="prod-price">{product.price}</span>
                    {product.size && (
                      <span className="product-size"> | {product.size}</span>
                    )}
                  </div>
                  <div className="product-cart-buttons">
                    <CoreButton
                      variant="outline"
                      size="small"
                      type="button"
                      onClick={() => onAddToCart && onAddToCart({ product })}
                    >
                      <span slot="content">
                        {getString("results.add_to_cart")}
                      </span>
                    </CoreButton>
                    <TfAddToList
                      label={getString("results.add_to_list")}
                      product={product}
                      className="tf-btn tf-btn-small tf-btn-default"
                    />
                  </div>
                </div>
              </div>
            );
          }

          return null;
        })}
      </Slider>
    </div>
  );
};

export default ProductCarousel;
